/// <amd-module name="Wiki/Overview/Components/OverviewContainer" />
import * as React from "react";
import { shallowCompare } from "VSS/Core/Util/Object";
import { equals } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { WikiPage } from "Wiki/Clients/Wiki/Contracts/Wiki";
import { IWikiEditorState } from "Wiki/Editor/Contracts";
import { IHeaderMetaDataState } from "Wiki/HeaderMetaData/Interfaces/IHeaderMetadataTypes";
import { ActionCreatorsHub } from "Wiki/Overview/Actions/ActionCreatorsHub";
import { Overview } from "Wiki/Overview/Components/Overview";
import { WikiPageSource } from "Wiki/Overview/Sources/WikiPageSource";
import { IOverviewState, OverviewStore } from "Wiki/Overview/Stores/OverviewStore";
import { OverviewActions, StoresHub } from "Wiki/Overview/Stores/StoresHub";
import { WikiTreeState, WikiTreeStore } from "Wiki/Overview/Stores/WikiTreeStore";
import { WikiPageSwitchTtiLogger } from "Wiki/Overview/Utils/WikiPageSwitchTtiLogger";
import { WikiPageMenuItemsProvider } from "Wiki/Overview/WikiPageMenuItemsProvider";
import { FilterTreeStore } from "Wiki/PageTree/Stores/FilterTreeStore";
import { ActionIds } from "Wiki/ViewCommon/Constants";
import { WikiInfo, WikiPageInfo, WikiViewOptions } from "Wiki/ViewCommon/Contracts";
import { Areas, PerformanceConstants, TelemetryConstants } from "Wiki/ViewCommon/CustomerIntelligenceConstants";
import { IStore } from "Wiki/ViewCommon/IStore";
import { publish } from "Wiki/ViewCommon/Utils/WikiHelper";

export interface IOverviewContainerProps {
    wikiViewOptions: WikiViewOptions;
    wikiInfo: WikiInfo;
    wikiPageInfo?: WikiPageInfo;
    wikiPages: WikiPage[];
    showPublishCodeWikiAction: boolean;
    showCreateNewProjectWikiAction: boolean;
    isHandleBrokenLinksEnabled: boolean;
    isEmptyWiki: boolean;
    isInitialTreeDataPopulated: boolean;
    isPageCommentsReadOnly: boolean;
}

export interface IOverviewContainerState {
    editorState: IWikiEditorState | undefined;
    overviewState: IOverviewState;
    treeState: WikiTreeState;
    headerMetaDataState: IHeaderMetaDataState | undefined;
}

export class OverviewContainer extends VssComponent<IOverviewContainerProps, IOverviewContainerState> {
    private _storesHub: StoresHub;
    private _actionsCreatorHub: ActionCreatorsHub;
    private _menuProvider: WikiPageMenuItemsProvider;
    private _wikiPageSwitchTtiLogger: WikiPageSwitchTtiLogger;
    private _publishLandedOnWikiHubTelemetry: boolean;

    constructor(props: IOverviewContainerProps, context: IVssComponentContext) {
        super(props, context);

        const overviewActions = new OverviewActions();
        this._storesHub = new StoresHub(
            {
                overviewStore: new OverviewStore(this.props.wikiViewOptions, this.props.wikiPageInfo),
                treeStore: new WikiTreeStore(
                    this.props.isInitialTreeDataPopulated,
                    this.props.wikiPages,
                    this.props.wikiPageInfo && this.props.wikiPageInfo.path
                ),
                filterStore: new FilterTreeStore()
            },
            overviewActions
        );
        this._publishLandedOnWikiHubTelemetry = true;
        this._wikiPageSwitchTtiLogger = new WikiPageSwitchTtiLogger(this.context.pageContext);
        this._actionsCreatorHub = new ActionCreatorsHub(
            this.context.pageContext,
            this.props.wikiInfo.wiki,
            this.props.wikiInfo.wikiVersion,
            this.props.wikiInfo.wikiRepositoryData.repository,
            {
                pageSource: new WikiPageSource(this.context.pageContext)
            },
            overviewActions,
            this._storesHub,
            this._addStoreListener,
            this.props.wikiViewOptions,
            this._wikiPageSwitchTtiLogger,
            this.props.isEmptyWiki
        );

        this.state = {
            overviewState: this._storesHub.stores.overviewStore.getState(),
            editorState: undefined,

            treeState: this._storesHub.stores.treeStore.getState(),
            headerMetaDataState: undefined
            // currently we do not listen to filterStore
        };

        this._menuProvider = new WikiPageMenuItemsProvider(
            this.context.pageContext,
            this.props.wikiInfo.wiki,
            this.props.wikiInfo.wikiVersion,
            this._actionsCreatorHub,
            this._storesHub.getState,
            this.props.wikiInfo.wikiPermissions,
            this.props.isHandleBrokenLinksEnabled
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        const performanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Areas.Wiki, PerformanceConstants.Overview, undefined, { isFirstRender: true });

        this._storesHub.stores.overviewStore.addChangedListener(this._updateStateFromStore);
        this._storesHub.stores.treeStore.addChangedListener(this._updateStateFromStore);

        this._actionsCreatorHub.loadHeaderMetadata();
        this._actionsCreatorHub.treeAC.loadTreeIfRequired();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        this._storesHub.stores.overviewStore.removeChangedListener(this._updateStateFromStore);
        this._storesHub.stores.treeStore.removeChangedListener(this._updateStateFromStore);
        this._storesHub.asyncEditorStore && this._storesHub.asyncEditorStore.removeChangedListener(this._updateStateFromStore);
        this._storesHub.asyncHeaderMetaDataStore && this._storesHub.asyncHeaderMetaDataStore.removeChangedListener(this._updateStateFromStore);
        this._storesHub.removeActionListeners();
    }

    public componentDidUpdate(prevProps: Readonly<IOverviewContainerProps>, prevState: Readonly<IOverviewContainerState>): void {
        super.componentDidUpdate(prevProps, prevState);

        if (!shallowCompare(prevProps, this.props)) {
            const action = this.props.wikiViewOptions.action || ActionIds.View;
            const { pageInfo, anchor, isEditing } = this._storesHub.stores.overviewStore.getState();
            const prevAction = isEditing ? ActionIds.Edit : ActionIds.View;

            if (
                !shallowCompare(
                    { pagePath: pageInfo && pageInfo.path, id: pageInfo && pageInfo.id, action: prevAction }, // Old path and action
                    {
                        pagePath: this.props.wikiPageInfo && this.props.wikiPageInfo.path,
                        id: this.props.wikiPageInfo && this.props.wikiPageInfo.id,
                        action
                    } // New path and action
                )
            ) {
                if (equals(action, ActionIds.View)) {
                    if (this.props.wikiPageInfo) {
                        let wikiPage: WikiPage | undefined = undefined;
                        // path can be undefined in some cases like back navigation to non existing page
                        // to show 404 experience set it to undefined page
                        if (this.props.wikiPageInfo.path) {
                            wikiPage = { path: this.props.wikiPageInfo.path, id: this.props.wikiPageInfo.id } as WikiPage;
                        }

                        this._actionsCreatorHub.onPageSelected(wikiPage, true, true, this.props.wikiViewOptions.anchor, true);
                    }
                } else if (equals(action, ActionIds.Edit)) {
                    this.props.wikiPageInfo && this._actionsCreatorHub.startEditing(this.props.wikiPageInfo.path, true);
                }
            } else if (!equals(this.props.wikiViewOptions.anchor || "", anchor || "")) {
                if (this.props.wikiViewOptions.anchor) {
                    this._actionsCreatorHub.onAnchorClicked(this.props.wikiViewOptions.anchor, true);
                } else {
                    this._actionsCreatorHub.removeAnchor(true);
                }
            }
        }
    }

    public render(): JSX.Element {
        return (
            <Overview
                editorState={this.state.editorState}
                overviewState={this.state.overviewState}
                wikiInfo={this.props.wikiInfo}
                showCreateNewProjectWikiAction={this.props.showCreateNewProjectWikiAction}
                showPublishCodeWikiAction={this.props.showPublishCodeWikiAction}
                actionCreatorHub={this._actionsCreatorHub}
                treeState={this.state.treeState}
                filterStore={this._storesHub.stores.filterStore}
                headerMetaDataState={this.state.headerMetaDataState}
                menuItemProvider={this._menuProvider}
                isHandleBrokenLinksEnabled={this.props.isHandleBrokenLinksEnabled}
                wikiPageSwitchTtiLogger={this._wikiPageSwitchTtiLogger}
                isPageCommentsReadOnly={this.props.isPageCommentsReadOnly}
            />
        );
    }

    private _addStoreListener = (store: IStore): void => {
        store.addChangedListener(this._updateStateFromStore);
    };

    private _updateStateFromStore = (): void => {
        if (this._publishLandedOnWikiHubTelemetry) {
            publish(this.context.pageContext, TelemetryConstants.LandedOnWikiHub, this.props.wikiInfo.wiki.type, {
                isHomePage: this.state.treeState.homePagePath === (this.state.overviewState.pageInfo && this.state.overviewState.pageInfo.path)
            });
            this._publishLandedOnWikiHubTelemetry = false;
        }
        this.setState({
            overviewState: this._storesHub.stores.overviewStore.getState(),
            editorState: this._storesHub.asyncEditorStore && this._storesHub.asyncEditorStore.getState(),
            treeState: this._storesHub.stores.treeStore.getState(),
            headerMetaDataState: this._storesHub.asyncHeaderMetaDataStore && this._storesHub.asyncHeaderMetaDataStore.getState()
        });
    };
}
